import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";
import AppFooter from "@/components/AppFooter";

import { useTranslation } from "react-i18next";
/** Privacy Policy — DPDP Act 2023 (India) aligned. Explains what we collect,
 *  why, how long we keep it, who we share it with, and how users exercise their
 *  data-principal rights. Refresh the "Last updated" line on material changes. */
export default function Privacy() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="privacy-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <h1 className="ml-auto flex items-center gap-1.5 text-lg font-bold">
            <Shield className="h-4 w-4 text-primary" /> Privacy Policy
          </h1>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-8">
        <div className="rounded-2xl border border-border bg-card p-6 text-sm">
          <p className="text-xs text-muted-foreground">Last updated: 18 February 2026 · Complies with India's DPDP Act 2023</p>

          <section className="prose prose-slate mt-6 max-w-none text-sm dark:prose-invert">
            <h2>1. Who we are</h2>
            <p>HisabX ("we", "us") is operated by HisabX Technologies, Pune, Maharashtra, India. This policy explains how we collect, use, share, and protect personal data of dealers, managers, CSAs, credit customers, and CAs who use the HisabX platform.</p>

            <h2>2. Data we collect</h2>
            <ul>
              <li><b>{t("legal.privacy.identity")}</b> — name, phone number, role (Dealer / Manager / CSA / CA / Credit customer), email if provided, hashed password/PIN.</li>
              <li><b>{t("legal.privacy.business_data")}</b> — pump name and address, dispenser configuration, HISABs, cashbook entries, credit customer statements, staff attendance records, shift photos.</li>
              <li><b>{t("legal.privacy.operational_uploads")}</b> — printer-receipt images, fleet-card slips, cash-handover photos, staff selfies + GPS coordinates at check-in.</li>
              <li><b>{t("legal.privacy.device_usage_data")}</b> — IP address, user-agent, session timestamps, error logs (via Sentry, anonymised).</li>
              <li><b>{t("legal.privacy.communications_metadata")}</b> — Telegram chat IDs (if linked), WhatsApp opt-in status, email delivery receipts from Resend.</li>
              <li><b>{t("legal.privacy.ai_processing_inputs")}</b> — 30-day aggregate KPIs of your pump are sent to our AI provider(s) for insight generation. No CSA names or customer PII are included in AI payloads.</li>
            </ul>

            <h2>3. Why we process it</h2>
            <ul>
              <li>{t("legal.privacy.deliver_core_service_hisab_verification")}</li>
              <li>{t("legal.privacy.secure_your_account_brute_force_lockout")}</li>
              <li>{t("legal.privacy.send_transactional_notifications_you_hav")}</li>
              <li>{t("legal.privacy.provide_ai_insights_and_ocr_with_the_saf")}</li>
              <li>{t("legal.privacy.meet_legal_obligations_statutory_financi")}</li>
              <li>{t("legal.privacy.improve_the_product_aggregated_anonymise")}</li>
            </ul>

            <h2>4. Legal basis (DPDP Act 2023)</h2>
            <p>We rely on: (a) your consent, taken at sign-up and per-feature (WhatsApp/Telegram opt-in, GPS at check-in); (b) legitimate uses under Section 7 of the Act — service provision, employment, and legal compliance; and (c) contractual necessity for paid subscriptions.</p>

            <h2>5. Who we share data with</h2>
            <p>We <b>never sell</b> personal data. We share only with these categories of processors, strictly on your behalf and under written contracts:</p>
            <ul>
              <li><b>{t("legal.privacy.resend")}</b> — transactional email delivery (invites, password resets).</li>
              <li><b>{t("legal.privacy.twilio")}</b> — WhatsApp Business messaging for opt-in alerts.</li>
              <li><b>{t("legal.privacy.telegram")}</b> — bot notifications you have connected.</li>
              <li><b>{t("legal.privacy.emergent_object_storage")}</b> — uploaded images.</li>
              <li><b>Google / Gemini + emergentintegrations</b> — OCR and AI insights on aggregated data.</li>
              <li><b>{t("legal.privacy.sentry")}</b> — anonymised error monitoring (if configured).</li>
              <li><b>{t("legal.privacy.law_enforcement")}</b> — only under a valid Indian court order or lawful government direction.</li>
              <li><b>{t("legal.privacy.chartered_accountants")}</b> — only if you invite them via the CA Add-on; their access is read-only and audited.</li>
            </ul>

            <h2>6. Data location and transfers</h2>
            <p>{t("legal.privacy.primary_data_is_stored_in_india_mongo_at")}</p>

            <h2>7. Retention</h2>
            <ul>
              <li>{t("legal.privacy.live_operational_data_kept_as_long_as_yo")}</li>
              <li>{t("legal.privacy.financial_records_retained_for_8_years_t")}</li>
              <li>Refresh tokens &amp; password-reset tokens — auto-expire via TTL indexes (7 days / 30 min respectively).</li>
              <li>{t("legal.privacy.backup_zips_retained_30_days_auto_or_as")}</li>
            </ul>

            <h2>8. Your rights as a data principal</h2>
            <p>{t("legal.privacy.under_the_dpdp_act_2023_you_may")}</p>
            <ul>
              <li>{t("legal.privacy.access_your_personal_data_any_time_from")}</li>
              <li>{t("legal.privacy.request_correction_or_updation_of_inaccu")}</li>
              <li>{t("legal.privacy.request_deletion_of_your_data_see")} <Link to="/delete-account" className="text-primary underline">/delete-account</Link>).</li>
              <li>{t("legal.privacy.withdraw_consent_for_optional_features_w")}</li>
              <li>{t("legal.privacy.nominate_a_person_to_exercise_these_righ")}</li>
              <li>{t("legal.privacy.lodge_a_complaint_with_the_data_protecti")}</li>
            </ul>
            <p>{t("legal.privacy.to_exercise_any_right_email")} <span className="font-mono">privacy@hisabx.in</span> from your registered address. We respond within 30 days.</p>

            <h2>9. Security measures</h2>
            <ul>
              <li>{t("legal.privacy.passwords_hashed_with_bcrypt_cost_12")}</li>
              <li>Sessions delivered via httpOnly + Secure + SameSite=None cookies.</li>
              <li>{t("legal.privacy.refresh_token_rotation_with_family_reuse")}</li>
              <li>{t("legal.privacy.brute_force_lockout_10_failed_attempts_3")}</li>
              <li>{t("legal.privacy.url_masking_so_route_paths_don_t_leak_fe")}</li>
              <li>TLS 1.2+ in transit; AES-256 at rest.</li>
              <li>{t("legal.privacy.force_logout_tools_for_admins_after_any")}</li>
              <li>{t("legal.privacy.every_mutating_admin_action_written_to_a")}</li>
            </ul>

            <h2>10. Children's data</h2>
            <p>{t("legal.privacy.hisabx_is_a_b2b_tool_and_not_directed_at")}</p>

            <h2>11. Cookies</h2>
            <p>We use only essential cookies for authentication (access + refresh tokens). No third-party ad or tracking cookies. No cross-site tracking.</p>

            <h2>12. Changes to this Policy</h2>
            <p>{t("legal.privacy.we_revise_this_policy_from_time_to_time")}</p>

            <h2>13. Contact</h2>
            <p>{t("legal.privacy.data_protection_queries")} <span className="font-mono">privacy@hisabx.in</span> · Grievance officer: <span className="font-mono">grievance@hisabx.in</span> · Postal: HisabX Technologies, Pune, Maharashtra, India.</p>
          </section>
        </div>
      </main>

      <AppFooter />
    </div>
  );
}
